import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { take } from 'rxjs/operators';
import { PrintBatchData } from '@app/services/batches/print-batches';
import { ScannableOrder } from '@app/services/orders/scannable-order';
import { PrintService } from '../../services/print.service';
import { ScanService } from '../../services/scan.service';
import { BatchOrdersModalComponent } from '../batch-orders-modal/batch-orders-modal.component';
import { ScanBatchSuccessComponent } from '../scan-batch-success/scan-batch-success.component';
import { ScanErrorComponent } from '../scan-error/scan-error.component';
import { ScanInputModalComponent } from '../scan-input-modal/scan-input-modal.component';
import { ScanOrderModalComponent } from '../scan-order-modal/scan-order-modal.component';
import { ScanOrderSuccessComponent } from '../scan-order-success/scan-order-success.component';
import { SelectScannedOrderComponent } from '../select-scanned-order/select-scanned-order.component';
import { Subscription } from 'rxjs';

/**
 * This component serves to avoid a circular dependency between ScanService and components used in Material Dialogs
 */
@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-scan-modals-container',
  templateUrl: './scan-modals-container.component.html',
  styleUrls: ['./scan-modals-container.component.scss'],
  standalone: false,
})
export class ScanModalsContainerComponent implements OnInit, OnDestroy {
  private scanInputDialogRef: MatDialogRef<ScanInputModalComponent>;
  private scanOrderDialogRef: MatDialogRef<ScanOrderModalComponent>;
  private scanErrorDialogRef: MatDialogRef<ScanErrorComponent>;
  private batchOrderdDialogRef: MatDialogRef<BatchOrdersModalComponent>;
  private scanOrderSuccessDialogRef: MatDialogRef<ScanOrderSuccessComponent>;
  private scanBatchSuccessDialogRef: MatDialogRef<ScanBatchSuccessComponent>;
  private scanSelectOrderDialogRef: MatDialogRef<SelectScannedOrderComponent>;
  private scanOrderSuccessDialogOpenedSubscription: Subscription;
  private batchOrdersDialogOpenedSubscription: Subscription;
  private scanInputDialogOpenedSubscription: Subscription;
  private scanOrderDialogOpenedSubscription: Subscription;
  private scanErrorSubscription: Subscription;
  private scanBatchSuccessDialogOpenedSubscription: Subscription;
  private scanSelectOrderDialogSubscription: Subscription;

  constructor(
    private matDialog: MatDialog,
    private scanService: ScanService,
    private printService: PrintService,
  ) {}

  ngOnInit(): void {
    this.scanOrderSuccessDialogOpenedSubscription = this.scanService.scanOrderSuccessDialogOpened$
      .pipe(untilDestroyed(this))
      .subscribe((order) => {
        order ? this.openScanOrderSuccessModal(order) : this.closeScanOrderSuccessModal();
      });
    this.batchOrdersDialogOpenedSubscription = this.printService.batchOrdersDialogOpened$
      .pipe(untilDestroyed(this))
      .subscribe((batchOrders) => {
        batchOrders ? this.openBatchOrdersDialog(batchOrders) : this.closeBatchOrdersDialog();
      });
    this.scanInputDialogOpenedSubscription = this.scanService.scanInputDialogOpened$
      .pipe(untilDestroyed(this))
      .subscribe((shouldOpen) => {
        shouldOpen ? this.openScanInputModal(shouldOpen) : this.closeScanInputModal();
      });

    this.scanOrderDialogOpenedSubscription = this.scanService.scanOrderDialogOpened$
      .pipe(untilDestroyed(this))
      .subscribe((order) => {
        order ? this.openScanOrderModal(order) : this.closeScanOrderModal();
      });
    this.scanErrorSubscription = this.scanService.scanError$.pipe(untilDestroyed(this)).subscribe((scanError) => {
      scanError ? this.openScanErrorModal(scanError) : null;
    });
    this.scanBatchSuccessDialogOpenedSubscription = this.scanService.scanBatchSuccessDialogOpened$
      .pipe(untilDestroyed(this))
      .subscribe((batch) => {
        this.closeScanBatchSuccessDialog();
      });
    this.scanSelectOrderDialogSubscription = this.scanService.scanSelectOrderDialogOpened$
      .pipe(untilDestroyed(this))
      .subscribe((orders) => {
        orders ? this.openScanSelectOrderDialog(orders) : this.closeScanSelectOrderDialog();
      });
  }

  ngOnDestroy(): void {
    this.scanOrderSuccessDialogOpenedSubscription.unsubscribe();
    this.batchOrdersDialogOpenedSubscription.unsubscribe();
    this.scanOrderDialogOpenedSubscription.unsubscribe();
    this.scanInputDialogOpenedSubscription.unsubscribe();
    this.scanErrorSubscription.unsubscribe();
    this.scanBatchSuccessDialogOpenedSubscription.unsubscribe();
    this.scanSelectOrderDialogSubscription.unsubscribe();
  }

  private openScanSelectOrderDialog(orders: ScannableOrder[]): void {
    if (!this.scanSelectOrderDialogRef) {
      this.scanSelectOrderDialogRef = this.matDialog.open(SelectScannedOrderComponent, {
        width: '32vw',
        height: '37vh',
        autoFocus: true,
      });
      const selectOrderDialogInstance = this.scanSelectOrderDialogRef.componentInstance;
      selectOrderDialogInstance.orders = orders;

      this.scanSelectOrderDialogRef
        .afterClosed()
        .pipe(take(1))
        .subscribe(() => {
          this.scanSelectOrderDialogRef = undefined;
        });
    }
  }

  private closeScanSelectOrderDialog(): void {
    this.scanSelectOrderDialogRef?.close();
  }

  private openBatchOrdersDialog(batchOrders: PrintBatchData): void {
    if (!this.batchOrderdDialogRef) {
      this.batchOrderdDialogRef = this.matDialog.open(BatchOrdersModalComponent, {
        minWidth: '760px',
        minHeight: '70vh',
        autoFocus: false,
      });
      const batchOrdersInstance = this.batchOrderdDialogRef.componentInstance;
      batchOrdersInstance.batchOrders = batchOrders;

      this.batchOrderdDialogRef
        .afterClosed()
        .pipe(take(1))
        .subscribe(() => {
          this.batchOrderdDialogRef = undefined;
          this.printService.resetCurrenttBatchOrders();
        });
    }
  }

  private closeBatchOrdersDialog(): void {
    this.batchOrderdDialogRef?.close();
  }

  private openScanBatchSuccessDialog(batch: PrintBatchData): void {
    if (!this.scanBatchSuccessDialogRef) {
      this.scanBatchSuccessDialogRef = this.matDialog.open(ScanBatchSuccessComponent, {
        minHeight: '100vh',
        minWidth: '100vw',
        width: '63vw',
        height: '65vh',
        autoFocus: true,
      });
      const scanBatchSuccessInstance = this.scanBatchSuccessDialogRef.componentInstance;
      scanBatchSuccessInstance.batch = batch;

      this.scanBatchSuccessDialogRef
        .afterClosed()
        .pipe(take(1))
        .subscribe(() => {
          this.scanBatchSuccessDialogRef = undefined;
          this.printService.scannedBatch = null;
        });
    }
  }

  private closeScanBatchSuccessDialog(): void {
    this.scanBatchSuccessDialogRef?.close();
  }

  private openScanInputModal(modalType: 'order' | 'sku'): void {
    if (!this.scanInputDialogRef) {
      this.scanInputDialogRef = this.matDialog.open(ScanInputModalComponent, {
        minWidth: '411px',
        minHeight: '11vh',
        autoFocus: true,
      });
      const scanInputInstance = this.scanInputDialogRef.componentInstance;
      scanInputInstance.modalType = modalType;
      this.scanInputDialogRef
        .afterClosed()
        .pipe(take(1))
        .subscribe(() => {
          this.scanInputDialogRef = undefined;
        });
    }
  }

  private closeScanInputModal(): void {
    this.scanInputDialogRef?.close();
  }

  private openScanOrderModal(order: ScannableOrder): void {
    if (!this.scanOrderDialogRef) {
      this.scanOrderDialogRef = this.matDialog.open(ScanOrderModalComponent, {
        maxWidth: '760px',
        minHeight: 'auto',
        autoFocus: false,
      });
      const scanOrderInstance = this.scanOrderDialogRef.componentInstance;
      scanOrderInstance.order = order;

      this.scanOrderDialogRef
        .afterClosed()
        .pipe(take(1))
        .subscribe(() => {
          this.scanOrderDialogRef = undefined;
        });
    }
  }

  private closeScanOrderModal(): void {
    this.scanOrderDialogRef?.close();
  }

  private openScanErrorModal(message: string): void {
    this.scanErrorDialogRef = this.matDialog.open(ScanErrorComponent, {
      width: '612px',
      height: '208px',
    });
    const scanErrorInstance = this.scanErrorDialogRef.componentInstance;
    // message = "abc/ndef<br>ghi"
    scanErrorInstance.message = message;
  }

  private openScanOrderSuccessModal(order: ScannableOrder): void {
    this.scanOrderSuccessDialogRef = this.matDialog.open(ScanOrderSuccessComponent, {
      maxWidth: '90vw',
      maxHeight: '90vh',
      width: '630px',
      height: '77vh',
      minHeight: '600px',
    });
    const scanOrderSuccessInstance = this.scanOrderSuccessDialogRef.componentInstance;
    scanOrderSuccessInstance.order = order;
  }

  private closeScanOrderSuccessModal(): void {
    this.scanOrderSuccessDialogRef.close();
  }
}
